import { h, TargetedKeyboardEvent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { loginValidationRules } from './validate';
import { useCoreContext } from '../../../../../core/Context/CoreProvider';
import { useFormWithA11y } from '../../../../../utils/useForm';
import Field from '../../../FormFields/Field';
import InputEmail from '../../../FormFields/InputEmail';
import { setFocusOnField } from '../../../../../utils/setFocus';

type OnChangeProps = { data: CtPLoginInputDataState; valid; errors; isValid: boolean };

interface CtPLoginInputProps {
    disabled: boolean;
    errorMessage?: string;
    onPressEnter(): Promise<void>;
    onChange({ data, valid, errors, isValid }: OnChangeProps): void;
    onSetInputHandlers(handlers: CtPLoginInputHandlers): void;
}

interface CtPLoginInputDataState {
    shopperLogin?: string;
}

export type CtPLoginInputHandlers = {
    validateInput(): void;
    focusInput(): void;
};

const CtPLoginInput = (props: Readonly<CtPLoginInputProps>): h.JSX.Element => {
    const { i18n } = useCoreContext();
    const formSchema = ['shopperLogin'];
    const containerRef = useRef<HTMLDivElement>(null);
    const { handleChangeFor, data, triggerValidation, valid, errors, isValid } = useFormWithA11y<CtPLoginInputDataState>({
        schema: formSchema,
        rules: loginValidationRules,
        formHolder: containerRef
    });
    const loginInputHandlersRef = useRef<CtPLoginInputHandlers>({ validateInput: null, focusInput: null });
    const [isLoginInputDirty, setIsLoginInputDirty] = useState<boolean>(false);

    const validateInput = useCallback(() => {
        setIsLoginInputDirty(true);
        triggerValidation();
    }, [triggerValidation]);

    useEffect(() => {
        if (containerRef.current && props.errorMessage) {
            setFocusOnField(containerRef.current, 'shopperLogin');
        }
    }, [props.errorMessage]);

    useEffect(() => {
        if (data.shopperLogin) setIsLoginInputDirty(true);
    }, [data.shopperLogin]);

    useEffect(() => {
        loginInputHandlersRef.current.validateInput = validateInput;
        props.onSetInputHandlers(loginInputHandlersRef.current);
    }, [validateInput, props.onSetInputHandlers]);

    const handleOnKeyDown = useCallback(
        (event: TargetedKeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                void props.onPressEnter();
            }
        },
        [props.onPressEnter]
    );

    useEffect(() => {
        props.onChange({ data, valid, errors, isValid });
    }, [data, valid, errors]);

    return (
        <div ref={containerRef}>
            <Field
                name="shopperLogin"
                label={i18n.get('ctp.login.inputLabel')}
                errorMessage={
                    isLoginInputDirty
                        ? props.errorMessage ||
                          (errors.shopperLogin && typeof errors.shopperLogin?.errorMessage === 'string'
                              ? i18n.get(errors.shopperLogin.errorMessage)
                              : null)
                        : null
                }
                classNameModifiers={['shopperLogin']}
                errorLive={true}
            >
                <InputEmail
                    name={'shopperLogin'}
                    autocorrect={'off'}
                    spellcheck={false}
                    value={data.shopperLogin}
                    disabled={props.disabled}
                    onInput={handleChangeFor('shopperLogin', 'input')}
                    onBlur={handleChangeFor('shopperLogin', 'blur')}
                    onKeyDown={handleOnKeyDown}
                    autocomplete={'email'}
                />
            </Field>
        </div>
    );
};

export default CtPLoginInput;
